import { type ReactNode, useLayoutEffect } from "react";

import { useHelpDrawer } from "./HelpDrawerContext";

interface Props {
  content: ReactNode;
}

/**
 * HelpDrawerContent component that sets help drawer content declaratively.
 *
 * @example
 * ```tsx
 * export function MyComponent() {
 *   return (
 *     <>
 *       <HelpDrawerContent content={<div>Help text here</div>} />
 *       <div>Your component content</div>
 *     </>
 *   );
 * }
 * ```
 */
export function HelpDrawerContent({ content }: Props) {
  const { setContent, resetContent } = useHelpDrawer();

  // Set content synchronously before paint to avoid flicker
  useLayoutEffect(() => {
    setContent(content);
    // Clear content when component unmounts
    return () => {
      resetContent();
    };
  }, [content, setContent, resetContent]);

  // This component doesn't render anything
  return null;
}
